import {
  BootstrapState,
  BootstrapStateMessages,
  getBootstrapStateNumber,
  getBootstrapProgress
} from './enums';
import { BootstrapRepository, BootstrapStateData } from './BootstrapRepository';
import ShellLocalStorageDAOImpl from '../../../dao/ShellLocalStorageDAOImpl';
import { internalLogger } from '../../../interface/v1/clientOsLogger';

export interface BootstrapManagerOptions {
  enableSanitizer?: boolean;
  onStateChange?: (stateData: BootstrapStateData) => void;
  disableLogging?: boolean;
  disableStateChangeNotification?: boolean;
}

/**
 * BootstrapManager manages the initialization state of the shell-commons library
 * and provides methods to track the bootstrap process phases.
 */
export class BootstrapManager {
  private repository: BootstrapRepository;
  private onStateChange?: (stateData: BootstrapStateData) => void;
  private currentState: BootstrapState = BootstrapState.IDLE;
  private disableLogging: boolean;
  private disableStateChangeNotification: boolean;
  private initialTimestamp: number;

  constructor(options: BootstrapManagerOptions) {
    const shellDAO = new ShellLocalStorageDAOImpl();
    this.initialTimestamp = Date.now();

    this.repository = new BootstrapRepository(
      shellDAO,
      options.enableSanitizer || false
    );
    this.onStateChange = options.onStateChange;
    this.disableLogging = options.disableLogging || false;
    this.disableStateChangeNotification =
      options.disableStateChangeNotification || false;

    // Initialize with existing state if available
    const existingState = this.repository.getState();
    if (existingState) {
      this.currentState = existingState.state;
    }
  }

  /**
   * Sets the current bootstrap state
   * @param state - The bootstrap state to set
   * @param message - Optional custom message (defaults to predefined message)
   * @param error - Optional error message if state is ERROR
   */
  setState(state: BootstrapState, message?: string, error?: string): void {
    const stateData: BootstrapStateData = {
      state,
      timestamp: Date.now(),
      message: message || BootstrapStateMessages[state],
      error
    };

    // Log only if logging is enabled
    if (!this.disableLogging) {
      /* istanbul ignore next */
      internalLogger.debug?.d1({
        message:
          '[BootstrapManager] Bootstrap state was changed. ' +
          stateData.message,
        variableName: 'state',
        currentValue: state,
        diffTimestamp: Date.now() - this.initialTimestamp
      });
    }

    this.currentState = state;
    this.repository.setState(stateData);

    // Notify listeners of state change only if notifications are enabled
    if (!this.disableStateChangeNotification && this.onStateChange) {
      this.onStateChange(stateData);
    }
  }

  /**
   * Gets the current bootstrap state data
   * @returns Current bootstrap state data or null if not set
   */
  getState(): BootstrapStateData | null {
    return this.repository.getState();
  }

  /**
   * Gets just the current bootstrap state enum value
   * @returns Current bootstrap state
   */
  getCurrentState(): BootstrapState {
    return this.currentState;
  }

  /**
   * Gets the message for the current state
   * @returns Message for current state
   */
  getCurrentMessage(): string {
    const stateData = this.getState();
    return stateData?.message || BootstrapStateMessages[this.currentState];
  }

  /**
   * Checks if the bootstrap process is in a loading state
   * @returns True if bootstrap is in progress
   */
  isLoading(): boolean {
    const loadingStates = [
      BootstrapState.CONFIGURATION_LOADING,
      BootstrapState.INSTANTIATION,
      BootstrapState.CONFIGURATION,
      BootstrapState.CRITICAL_SERVICES_INIT,
      BootstrapState.RUNTIME_CONFIG_BY_BEHAVIOR,
      BootstrapState.INITIALIZATION,
      BootstrapState.INTEGRATION,
      BootstrapState.NON_CANONICAL_INITIALIZATION,
      BootstrapState.POST_INTEGRATION
    ];

    return loadingStates.includes(this.currentState);
  }

  /**
   * Checks if the bootstrap process completed successfully
   * @returns True if bootstrap completed successfully
   */
  isSuccess(): boolean {
    return this.currentState === BootstrapState.SUCCESS;
  }

  /**
   * Checks if the bootstrap process failed
   * @returns True if bootstrap failed
   */
  isError(): boolean {
    return this.currentState === BootstrapState.ERROR;
  }

  /**
   * Checks if the bootstrap process is idle (not started)
   * @returns True if bootstrap is idle
   */
  isIdle(): boolean {
    return this.currentState === BootstrapState.IDLE;
  }

  /**
   * Resets the bootstrap state to idle and clears storage
   */
  reset(): void {
    this.currentState = BootstrapState.IDLE;
    this.repository.clearState();
  }

  /**
   * Gets the numeric representation of the current state
   * @returns Numeric value representing the current state (0-9, or -1 for error)
   */
  getCurrentStateNumber(): number {
    return getBootstrapStateNumber(this.currentState);
  }

  /**
   * Gets the current progress percentage based on the state
   * @returns Progress percentage (0-100), or -1 for error state
   */
  getCurrentProgress(): number {
    return getBootstrapProgress(this.currentState);
  }

  /**
   * Compares the current state with another state numerically
   * @param otherState - The state to compare with
   * @returns Negative if current < other, 0 if equal, positive if current > other
   */
  compareState(otherState: BootstrapState): number {
    const currentNumber = getBootstrapStateNumber(this.currentState);
    const otherNumber = getBootstrapStateNumber(otherState);

    // Handle error state comparison specially
    if (currentNumber === -1 && otherNumber === -1) return 0;
    if (currentNumber === -1) return -1;
    if (otherNumber === -1) return 1;

    return currentNumber - otherNumber;
  }

  /**
   * Checks if the current state is before another state in the initialization sequence
   * @param targetState - The state to compare with
   * @returns True if current state comes before target state
   */
  isStateBefore(targetState: BootstrapState): boolean {
    return this.compareState(targetState) < 0;
  }

  /**
   * Checks if the current state is after another state in the initialization sequence
   * @param targetState - The state to compare with
   * @returns True if current state comes after target state
   */
  isStateAfter(targetState: BootstrapState): boolean {
    return this.compareState(targetState) > 0;
  }

  /**
   * Sets state to loading configuration phase
   */
  setConfigurationLoading(message?: string): void {
    this.setState(BootstrapState.CONFIGURATION_LOADING, message);
  }

  /**
   * Sets state to instantiation phase
   */
  setInstantiation(message?: string): void {
    this.setState(BootstrapState.INSTANTIATION, message);
  }

  /**
   * Sets state to configuration phase
   */
  setConfiguration(message?: string): void {
    this.setState(BootstrapState.CONFIGURATION, message);
  }

  /**
   * Sets state to critical services initialization phase
   */
  setCriticalServicesInit(message?: string): void {
    this.setState(BootstrapState.CRITICAL_SERVICES_INIT, message);
  }

  /**
   * Sets state to pre-configuration phase
   */
  setPreConfiguration(message?: string): void {
    this.setState(BootstrapState.RUNTIME_CONFIG_BY_BEHAVIOR, message);
  }

  /**
   * Sets state to initialization phase
   */
  setInitialization(message?: string): void {
    this.setState(BootstrapState.INITIALIZATION, message);
  }

  /**
   * Sets state to integration phase
   */
  setIntegration(message?: string): void {
    this.setState(BootstrapState.INTEGRATION, message);
  }

  /**
   * Sets state to non-canonical initialization phase
   */
  setNonCanonicalInitialization(message?: string): void {
    this.setState(BootstrapState.NON_CANONICAL_INITIALIZATION, message);
  }

  /**
   * Sets state to post-integration phase
   */
  setPostIntegration(message?: string): void {
    this.setState(BootstrapState.POST_INTEGRATION, message);
  }

  /**
   * Sets state to success
   */
  setSuccess(message?: string): void {
    this.setState(BootstrapState.SUCCESS, message);
  }

  /**
   * Sets state to error
   */
  setError(error: string, message?: string): void {
    this.setState(BootstrapState.ERROR, message, error);
  }

  /**
   * Gets the last error if the state is ERROR
   * @returns Error message or null if no error
   */
  getLastError(): string | null {
    const stateData = this.getState();
    return stateData?.error || null;
  }

  /**
   * Checks if logging is disabled
   * @returns True if logging is disabled
   */
  isLoggingDisabled(): boolean {
    return this.disableLogging;
  }

  /**
   * Checks if state change notifications are disabled
   * @returns True if state change notifications are disabled
   */
  isStateChangeNotificationDisabled(): boolean {
    return this.disableStateChangeNotification;
  }

  /**
   * Enables or disables logging at runtime
   * @param disabled - True to disable logging, false to enable
   */
  setLoggingDisabled(disabled: boolean): void {
    this.disableLogging = disabled;
  }

  /**
   * Enables or disables state change notifications at runtime
   * @param disabled - True to disable notifications, false to enable
   */
  setStateChangeNotificationDisabled(disabled: boolean): void {
    this.disableStateChangeNotification = disabled;
  }

  /**
   * Sets a new state change callback at runtime
   * @param callback - New callback function or undefined to remove
   */
  setOnStateChangeCallback(
    callback?: (stateData: BootstrapStateData) => void
  ): void {
    this.onStateChange = callback;
  }
}
